/**
 * Detection and recovery for failed dynamic-module ("chunk") loads.
 *
 * Every page here is code-split behind `React.lazy(() => import(...))`. Each deploy rotates
 * every chunk's content hash and Firebase Hosting serves only the current release, so a
 * browser still holding an older `index.html` requests a chunk that no longer exists. The SPA
 * catch-all rewrite answers that request with `index.html` — HTTP 200, `text/html` — which the
 * browser rejects as a non-JS module script. Recovery is to get onto the current `index.html`,
 * which references chunks that do exist.
 */

/** Lower-cased fragments of the messages browsers and Vite emit when a module chunk fails to load. */
const CHUNK_ERROR_SIGNATURES = [
  'failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'importing a module script failed', // Safari
  'failed to load module script', // Chromium — wrong MIME type, i.e. the index.html fallback
  'unable to preload css for', // Vite preload helper, stylesheet dependency
];

/** sessionStorage key for the reload-attempt guard. Per-tab by design: a new tab gets a fresh budget. */
const RECOVERY_KEY = 'chunk_recovery_v1';

/** A reload started less than this ago is still landing — don't stack another on top of it. */
export const CHUNK_RECOVERY_IN_FLIGHT_MS = 5_000;

/** Automatic reloads allowed within `CHUNK_RECOVERY_WINDOW_MS` before we give up and show the fallback UI. */
export const CHUNK_RECOVERY_MAX_RELOADS = 2;

/** How long attempts are remembered; past this the budget re-arms for the next deploy. */
export const CHUNK_RECOVERY_WINDOW_MS = 10 * 60 * 1000;

/** Cap on `cause` links walked, so a cyclic cause chain can't spin. */
const MAX_CAUSE_DEPTH = 5;

/** One tab's automatic-reload history: how many we've fired and when the last one started. */
export interface ChunkRecoveryRecord {
  count: number;
  at: number;
}

export type ChunkRecoveryDecision = 'reload' | 'in-flight' | 'exhausted';

function matchesChunkSignature(message: string): boolean {
  const lowered = message.toLowerCase();
  return CHUNK_ERROR_SIGNATURES.some((signature) => lowered.includes(signature));
}

/**
 * True when `error` is a failed dynamic import/preload rather than a real application crash.
 * Message matching is unavoidable — the browser throws a plain `TypeError` with no code — and
 * it must stay strict: Vite routes module-*evaluation* errors through the same
 * `vite:preloadError` event, and reloading on one of those would loop.
 */
export function isChunkLoadError(error: unknown): boolean {
  let current: unknown = error;

  for (
    let depth = 0;
    depth <= MAX_CAUSE_DEPTH && current !== null && current !== undefined;
    depth += 1
  ) {
    const message =
      typeof current === 'string' ? current : current instanceof Error ? current.message : '';

    if (message !== '' && matchesChunkSignature(message)) return true;

    current = typeof current === 'object' ? (current as { cause?: unknown }).cause : undefined;
  }

  return false;
}

/**
 * Decide whether an automatic reload is safe right now. Pure so the loop guard — the one piece
 * that must never be wrong — is directly testable.
 */
export function decideChunkRecovery(
  record: ChunkRecoveryRecord | null,
  now: number,
): ChunkRecoveryDecision {
  if (record !== null) {
    const elapsed = now - record.at;

    // Negative elapsed means the clock moved backwards, so we can't measure how long ago the
    // last attempt was: fail closed rather than risk a loop we can't bound.
    if (elapsed < CHUNK_RECOVERY_IN_FLIGHT_MS) return 'in-flight';
    if (elapsed < CHUNK_RECOVERY_WINDOW_MS && record.count >= CHUNK_RECOVERY_MAX_RELOADS) {
      return 'exhausted';
    }
  }

  return 'reload';
}

function readRecoveryRecord(): ChunkRecoveryRecord | null {
  try {
    const raw = sessionStorage.getItem(RECOVERY_KEY);
    if (raw === null) return null;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;

    const { count, at } = parsed as Partial<ChunkRecoveryRecord>;
    return typeof count === 'number' && typeof at === 'number' ? { count, at } : null;
  } catch {
    // Corrupt or unreadable guard — treat as "no attempts yet". The write below is what
    // actually prevents a loop, and it fails closed when storage is unavailable.
    return null;
  }
}

function writeRecoveryRecord(record: ChunkRecoveryRecord): boolean {
  try {
    sessionStorage.setItem(RECOVERY_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false; // storage unavailable (private mode, quota) — caller must not reload
  }
}

/**
 * Reload onto a freshly fetched document. `cache: 'reload'` bypasses the HTTP cache for the
 * fetch *and* stores the response, so the following `location.reload()` — which only
 * revalidates, and treats a `max-age=3600` copy as fresh — is guaranteed to parse the current
 * `index.html` instead of the stale one that points at deleted chunks.
 */
export async function reloadWithFreshDocument(): Promise<void> {
  try {
    await fetch(window.location.href, { cache: 'reload' });
  } catch (error) {
    // Couldn't refresh the cached copy (offline, or the document request itself failed). Log
    // it and reload anyway — a plain reload is still the user's best move from here.
    console.warn('[chunk-recovery] could not pre-fetch a fresh document', error);
  }

  window.location.reload();
}

/**
 * Reload once to recover from a stale-deploy chunk error, subject to the loop guard. Safe to
 * call from several places for the same incident — the `in-flight` window makes the extra
 * calls no-ops.
 */
export async function attemptChunkRecovery(): Promise<ChunkRecoveryDecision> {
  const now = Date.now();
  const record = readRecoveryRecord();
  const decision = decideChunkRecovery(record, now);

  if (decision !== 'reload') return decision;

  const nextCount =
    record !== null && now - record.at < CHUNK_RECOVERY_WINDOW_MS ? record.count + 1 : 1;

  // Persist before reloading: without a durable guard the next load could fail identically and
  // reload forever, so unavailable storage counts as exhausted.
  if (!writeRecoveryRecord({ count: nextCount, at: now })) return 'exhausted';

  await reloadWithFreshDocument();
  return 'reload';
}
